import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Mention from '@tiptap/extension-mention';
import { Box, Button } from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import CloseIcon from '@mui/icons-material/Close';
import React, { useState, useRef, useCallback } from 'react';
import './CommentEditor.css';
import suggestion from './suggestion';
import type { MentionUser } from '../types';
import useCommentUploads from '../hooks/useCommentUploads';
import {
  ImageWithOriginalSrc,
  LinkWithOriginalHref,
} from '../../detail-form/richTextEditorExtensions';
import { normalizeMediaBeforeSave } from '../../detail-form/richTextMediaHelpers';
import { RichTextToolbar } from '../../detail-form/RichTextToolbar';
import { RichTextEditorFrame } from '../../detail-form/RichTextEditorFrame';

interface CommentEditorProps {
  onSend: (content: string, tags: string[]) => void;
  label?: string;
  onCancel?: () => void;
  isSubmitting?: boolean;
  initialContent?: string;
  availableUsers?: MentionUser[];
  submitLabel?: string;
  uploadContextId?: string | number;
}

export const CommentEditor = ({
  onSend,
  label,
  onCancel,
  isSubmitting,
  initialContent = '',
  availableUsers = [],
  submitLabel = 'Send',
  uploadContextId,
}: CommentEditorProps) => {
  const [tags, setTags] = useState<string[]>([]);
  const [isEditorEmpty, setIsEditorEmpty] = useState(initialContent.trim() === '');
  const labelId = React.useId();

  const imageInputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const editor = useEditor({
    extensions: [
      StarterKit as any,
      ImageWithOriginalSrc as any,
      LinkWithOriginalHref.configure({
        openOnClick: true,
        autolink: true,
      }) as any,
      Mention.configure({
        HTMLAttributes: { class: 'mention' },
        suggestion: {
          ...suggestion,
          items: ({ query }: { query: string }) =>
            availableUsers
              .filter((user) => user.name.toLowerCase().includes(query.toLowerCase()))
              .slice(0, 5),
        },
      }),
    ],
    content: initialContent,
    editorProps: {
      attributes: label ? { 'aria-labelledby': labelId } : {},
    },
    onUpdate({ editor }) {
      const mentions: string[] = [];
      editor.state.doc.descendants((node: any) => {
        if (node.type.name === 'mention') mentions.push(node.attrs.id);
      });
      setTags(mentions);
      const hasText = editor.getText().trim() !== '';
      const hasImages = editor.getHTML().includes('<img');
      setIsEditorEmpty(!hasText && !hasImages);
    },
  });

  const { uploadAndInsertFile } = useCommentUploads(editor, uploadContextId);

  const handleSend = () => {
    if (!editor) return;
    const html = normalizeMediaBeforeSave(editor.getHTML());
    if (editor.getText().trim() === '' && !html.includes('<img')) return;
    onSend(html, tags);
    editor.commands.clearContent();
    setTags([]);
    setIsEditorEmpty(true);
  };

  const handleImageInputChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file || !editor) return;
      try {
        await uploadAndInsertFile(file);
      } catch (err) {
        console.error('Image upload failed', err);
      } finally {
        if (e.target) e.target.value = '';
      }
    },
    [editor, uploadAndInsertFile]
  );

  const handleFileInputChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file || !editor) return;
      try {
        await uploadAndInsertFile(file);
      } catch (err) {
        console.error('File upload failed', err);
      } finally {
        if (e.target) e.target.value = '';
      }
    },
    [editor, uploadAndInsertFile]
  );

  if (!editor) return null;

  return (
    <Box className="comment-editor-root">
      <RichTextEditorFrame
        label={label}
        labelId={label ? labelId : undefined}
        toolbar={
          <RichTextToolbar
            editor={editor}
            enableMediaUpload={!!uploadContextId}
            canUploadMedia={!!uploadContextId}
            isFlushingPending={false}
            resolvedUploadContextId={uploadContextId ? String(uploadContextId) : null}
            imageInputRef={imageInputRef}
            fileInputRef={fileInputRef}
            onImageInputChange={handleImageInputChange}
            onFileInputChange={handleFileInputChange}
          />
        }
      >
        <Box className="comment-editor-content" onClick={() => editor.commands.focus()}>
          <EditorContent editor={editor} className="tiptap-comment-editor" />
        </Box>
      </RichTextEditorFrame>
      {(!isEditorEmpty || onCancel) && (
        <Box className="comment-editor-actions">
          {onCancel && (
            <Button
              variant="outlined"
              size="small"
              onClick={onCancel}
              disabled={isSubmitting}
              startIcon={<CloseIcon fontSize="small" />}
            >
              Cancel
            </Button>
          )}
          <Button
            variant="contained"
            color="primary"
            size="small"
            onClick={handleSend}
            disabled={isSubmitting}
            sx={{
              flexShrink: 0,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.75,
            }}
          >
            {submitLabel === 'Send' ? (
              <SendOutlinedIcon fontSize="small" />
            ) : (
              <SaveIcon fontSize="small" />
            )}
            <span>{submitLabel === 'Send' ? 'Comment' : submitLabel}</span>
          </Button>
        </Box>
      )}
    </Box>
  );
};
